/**
 * quotas.ts — the pure decisions behind Settings → Sales's Quotas card
 * (Phase 2 / 2.3b, F-4 / U-3), kept out of the component so vitest can pin
 * them without a DOM.
 *
 * The card is rows = employees × ONE selected period. Each row is its own
 * write with its own actor (there is no "set them all" body): a typed number
 * is a PUT (upsert), a blank on a row that HAS a target is a DELETE (clear),
 * and a blank on a row that never had one is nothing at all. What the wire
 * holds and what the input shows are kept apart on purpose — R3's absent-vs-
 * zero distinction reaches this surface as blank-vs-"0".
 *
 * The roster is the directory's ACTIVE employees joined with the period's
 * existing quota rows: an employee with a quota but no directory row (a
 * leaver, or a status the picker filter hides) still renders, from the wire's
 * own employeeName, so no target anyone set is ever hidden from the person
 * who can clear it.
 */
import type { ApiEmployeeDirectoryRow } from '../../types'
import type { SalesQuota, UpsertSalesQuotaRequest, DeleteSalesQuotaRequest } from '../../../../api/types'
import type { SalesPeriod } from '../sales/salesPeriod'
import { parsePoundsToPence } from '../../../../lib/money'

export interface QuotaDraftRow {
  employeeId: string
  employeeName: string
  /** The input's text, in POUNDS; '' = no target (a clear on save if one exists). */
  value: string
  /** The row the server holds for this employee + period, or null. */
  stored: SalesQuota | null
}

/** Pounds text for an input from integer pence: whole pounds stay whole
 *  ("50000"), pence show two places ("1234.56"). Never a locale string — the
 *  input must parse back through parsePoundsToPence unchanged. */
export function poundsText(pence: number): string {
  return pence % 100 === 0 ? String(pence / 100) : (pence / 100).toFixed(2)
}

/** The draft the table opens with. Directory order first (the roster as HR
 *  lists it), then any quota row whose employee the roster lacks. */
export function draftFromRoster(roster: ApiEmployeeDirectoryRow[], quotas: SalesQuota[]): QuotaDraftRow[] {
  const byEmployee = new Map(quotas.map(q => [q.employeeId, q]))
  const rows: QuotaDraftRow[] = roster.map(e => {
    const stored = byEmployee.get(e.id) ?? null
    return { employeeId: e.id, employeeName: e.fullName, value: stored ? poundsText(stored.targetPence) : '', stored }
  })
  const seen = new Set(roster.map(e => e.id))
  for (const q of quotas) {
    if (seen.has(q.employeeId)) continue
    rows.push({ employeeId: q.employeeId, employeeName: q.employeeName || q.employeeId, value: poundsText(q.targetPence), stored: q })
  }
  return rows
}

/** Blank is valid (no target / clear); otherwise the money-input rule —
 *  non-negative, at most two decimals. "0" is valid: a real target of zero. */
export function quotaInputValid(v: string): boolean {
  return v.trim() === '' || parsePoundsToPence(v) !== null
}

export type QuotaWrite =
  | { kind: 'upsert'; body: UpsertSalesQuotaRequest }
  | { kind: 'clear'; body: DeleteSalesQuotaRequest }
  | null

/**
 * What saving this row would send, or null when nothing changed. A number
 * equal to the stored target is a no-op (the server would also write no
 * history row for it); a blank where nothing is stored is a no-op; a blank
 * where something IS stored is the clear.
 */
export function quotaWrite(row: QuotaDraftRow, period: SalesPeriod): QuotaWrite {
  if (!quotaInputValid(row.value)) return null
  const key = { employeeId: row.employeeId, periodKind: period.kind, periodStart: period.start }
  if (row.value.trim() === '') {
    return row.stored ? { kind: 'clear', body: key } : null
  }
  const pence = parsePoundsToPence(row.value)!
  if (row.stored && row.stored.targetPence === pence) return null
  return { kind: 'upsert', body: { ...key, targetPence: pence } }
}
